import React, { useEffect, useState } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import InputLayout from "../../../atoms/InputLayout";
import MuiTimePicker from "../../../atoms/MuiTimePicker";
import * as Yup from "yup";
import Error from "../../../atoms/Error";
import { postData, patchData, getData } from "../../../../services";
import { useCookies } from "react-cookie";
import toast from "react-hot-toast";
import {
  confimationStyles,
  errorStyles,
} from "../../../../assets/styles/toast";
import { Priorities, ProjectStatus } from "../../../../helpers/staticData";
import SelectDropdown from "../../../molecules/Dropdown";
import QuillEditor from "../../../organisms/QuillEditorBasic";
import TeamDropdown from "../../../organisms/TeamDropdown";
import { getEmployeeList } from "../../../../services/basicGets";
import { DateSelector } from "../../../organisms/MonthYearSelector";

const ProjectsForm = ({
  onFormDirty,
  isAdmin,
  initialData,
  onClose,
  refetchData,
  // employeeList,
  rolesList,
  teamList,
  BusinessSegmentList,
  clientList,
  refetchClientList,
  refetchTeamList,
  departmentList,
}) => {
  const [cookies] = useCookies(["e_t"]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [employeeList, setEmployeeList] = useState([]);
  const [projectConfig, setProjectConfig] = useState();

  const [emailInput, setEmailInput] = useState("");
  const [emailError, setEmailError] = useState("");
  const [teams, setTeams] = useState(teamList);
  //  const [client , setClient] = useState(clientList?.data);

  const employeeIds = initialData?.employee_info?.map((emp) => emp.employee_id); // Changed from employeesInfo
  const teamIds = initialData?.team_info?.map((team) => team.team_id) || []; // Changed from teamInfo and teamId
  const ownersIds = initialData?.owners_info?.map((owner) => owner.owner_id);

  function convertMinutesToHHMMSS(minutes) {
    // Convert total minutes to seconds
    let totalSeconds = Math.floor(minutes * 60);

    // Calculate hours, minutes, and seconds
    let hours = Math.floor(totalSeconds / 3600);
    let remainingMinutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = totalSeconds % 60;

    // Format with leading zeros
    let formattedTime =
      String(hours).padStart(2, "0") +
      ":" +
      String(remainingMinutes).padStart(2, "0") +
      ":" +
      String(seconds).padStart(2, "0");

    return formattedTime;
  }
  const frequencyOptions = [
    { label: "Weekly", value: "weekly" },
    { label: "Bi-Weekly", value: "biweekly" },
    { label: "Fortnightly", value: "fortnightly" },
    { label: "Monthly", value: "monthly" },
  ];

  const daysOfWeekOptions = [
    { label: "Monday", value: "Monday" },
    { label: "Tuesday", value: "Tuesday" },
    { label: "Wednesday", value: "Wednesday" },
    { label: "Thursday", value: "Thursday" },
    { label: "Friday", value: "Friday" },
    { label: "Saturday", value: "Saturday" },
    { label: "Sunday", value: "Sunday" },
  ];

  const daysOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  }));

  const daysOfhalfMonthOptions = Array.from({ length: 15 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  }));

  // Validation schema
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    owner_ids: Yup.array()
      .required("Project owner is required")
      .min(1, "At least one owner must be selected"),
    priority: Yup.string().required("Priority is required"),
    team_assigned: Yup.array(),
    // client_id: Yup.string().required("Client is required"),
    // .required("Assigning to a team is required") // Ensures the field is not undefined/null
    // .min(1, "At least one team must be selected"),
    // assigned_to: Yup.string().required("Assigning is required"),
    status: Yup.string().required("Status is required"),
    // business_segment: Yup.string().required("Business Segment is Required"),
    // description : Yup.string(),
    alert_email: Yup.array()
      .of(Yup.string().email("Invalid email format"))
      .min(0),
    frequency: Yup.string().nullable(),
    days_of_week: Yup.array()
      .of(Yup.string())
      .when("frequency", (frequency, schema) => {
        if (frequency?.[0] === "weekly") {
          return schema
            .min(1, "Select exactly one day")
            .max(1, "Only 1 day allowed")
            .required("Day selection is required");
        }
        if (frequency?.[0] === "biweekly") {
          return schema
            .min(2, "Select exactly 2 days")
            .max(2, "Select exactly 2 days")
            .required("Exactly 2 days required");
        }
        return schema.notRequired().default([]);
      }),

    days_of_month: Yup.array()
      .of(Yup.string())
      .when("frequency", (frequency, schema) => {
        if (frequency?.[0] === "monthly") {
          return schema
            .min(1, "Select exactly one date")
            .max(1, "Only 1 date allowed")
            .required("Date selection is required");
        }
        if (frequency?.[0] === "fortnightly") {
          return schema
            .min(2, "Select exactly 2 dates")
            .max(2, "Select exactly 2 dates")
            .required("Exactly 2 dates required");
          // return schema
          // .min(1, "Only 1 date allowed")
          // .max(1, "Only 1 date allowed")
          // .required("Date selection is required");
        }
        return schema.notRequired().default([]);
      }),
    time_of_day: Yup.string().when("frequency", (frequency, schema) => {
      if (frequencyOptions.map((o) => o.value).includes(frequency?.[0])) {
        return schema
          .min(1, "Time must be greater than 0")
          .required("Time is required");
      }
      return schema.notRequired();
    }),

    trigger_start_date: Yup.string().when("frequency", (frequency, schema) => {
      if (frequencyOptions.map((o) => o.value).includes(frequency?.[0])) {
        return schema.required("Trigger Start Date is required");
      }
      return schema.notRequired();
    }),

    trigger_end_date: Yup.string()
      .nullable()
      .test(
        "is-after-trigger-start",
        "Trigger End Date must be greater than Trigger Start Date",
        function (value) {
          const { trigger_start_date } = this.parent;

          // If no end date or no start date, don't throw error yet
          if (!value || !trigger_start_date) return true;

          return new Date(value) > new Date(trigger_start_date);
        }
      ),
  });

  useEffect(() => {
    (async () => {
      const resEmployee = await getEmployeeList({
        params: {
          ...(!isAdmin && { employee_id: cookies.euid }),
          status: JSON.stringify(["Active"]),
        },
        token: cookies.e_t,
      });
      setEmployeeList(resEmployee?.employeeResponseValueLabel);
      //  setFilteredAssignedTo(resEmployee?.employeeResponseValueLabel);
      // 2. Get Report Config (ProjectReportsGenerationSettings/GetByProjectId)
      if (initialData?.id) {
        try {
          const res = await getData({
            endpoint: "ProjectReportsGenerationSettings/GetByProjectId",
            token: cookies.e_t,
            params: { project_id: initialData?.id },
          });
          if (res?.data?.id) {
            // const rawDays = res?.data?.days_of_month || [];
            // let daysofmonth = [];
            // if (res?.data?.frequency === "fortnightly") {
            //   // Filter valid values 1–15
            //   const validDays = rawDays
            //     .map(Number)
            //     .filter((d) => d >= 1 && d <= 15);

            //   if (validDays?.length > 0) {
            //     // Pick smallest valid value
            //     daysofmonth = [Math.min(...validDays)];
            //   } else {
            //     daysofmonth = [];
            //   }
            // } else {
            //   // For all other frequencies, keep the array as-is
            //   daysofmonth = rawDays;
            // }

            // const datas = {
            //   ...res.data,
            //   days_of_month: daysofmonth,
            // };

            setProjectConfig(res.data);
          } else {
            setProjectConfig(undefined);
          }
        } catch (error) {
          console.error("Error in useEffect:", error);
        }
      }
    })();
  }, []);


  // Initial form values
  const initialValues = {
    name: initialData?.name || "",
    owner_ids: ownersIds || "",
    priority: initialData?.priority || "",
    // client_id: initialData?.clientId || null,
    employee_id: employeeIds || null,
    team_assigned: teamIds || null,
    status: initialData?.status || "Ongoing",
    business_segment: initialData?.business_segment_id || null,
    description: initialData?.description || "",
    alert_email: initialData?.alert_email || [],
    frequency: projectConfig?.frequency || null,
    days_of_week: projectConfig?.days_of_week || [],
    days_of_month: projectConfig?.days_of_month || [],
    time_of_day: projectConfig?.time_of_day || null,
    trigger_end_date: projectConfig?.end_date || null,
    trigger_start_date: projectConfig?.start_date || null,
  };

  // console.log("employeeList2", employeeList);

  const handleEmailKeyDown = (e, values, setFieldValue, setFieldTouched) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const email = emailInput.trim();

      if (!email) return;

      if (values.alert_email.includes(email)) {
        setEmailError("This email is already added");
        return;
      }

      setFieldValue("alert_email", [...values.alert_email, email]);
      setFieldTouched("alert_email", true); // Trigger Yup validation
      setEmailInput("");
      setEmailError("");
    } else if (
      e.key === "Backspace" &&
      !emailInput &&
      values.alert_email.length > 0
    ) {
      const newEmails = [...values.alert_email];
      newEmails.pop();
      setFieldValue("alert_email", newEmails);
    }
  };

  const removeEmail = (index, values, setFieldValue) => {
    const newEmails = values.alert_email.filter((_, i) => i !== index);
    setFieldValue("alert_email", newEmails);
  };
  // Create Employee
  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true);
    // let daysOfMonth = [...(values.days_of_month || [])];

    // if (daysOfMonth.length === 1) {
    //   const first = Number(daysOfMonth[0]);
    //   const second = first + 16; // e.g., 2 + 16 = 18
    //   daysOfMonth.push(second);
    // }

    // const datas = {
    //   ...values,
    //   days_of_month:
    //     values.frequency === "fortnightly"
    //       ? daysOfMonth
    //       : values?.days_of_month,
    // };

    try {
      const res = await postData({
        endpoint: "Project/createProject",
        data: {
          ...values,
          description: values.description,
          business_segment_id: values.business_segment,
          manager_id: null,
        },
        token: cookies.e_t,
      });

      if (res) {
        toast.success("Project Created Successfully", {
          style: confimationStyles,
          duration: 1000,
        });
        resetForm();
        onClose();
        refetchData();
      }
    } catch (err) {
      toast.error("Error Creating Project", { style: errorStyles });
    } finally {
      setIsSubmitting(false);
    }
  };

  function stripHtml(html) {
    // Check if html exists and is a string
    if (!html || typeof html !== "string") return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  }

  // Edit Employee
  const handleEdit = async (values) => {
    setIsSubmitting(true);
    // let daysOfMonth = [...(values.days_of_month || [])];

    // if (daysOfMonth?.length === 1) {
    //   const first = Number(daysOfMonth[0]);
    //   const second = first + 16;
    //   daysOfMonth.push(second);
    // }

    // const datas = {
    //   ...values,
    //   days_of_month:
    //     values.frequency === "fortnightly"
    //       ? daysOfMonth
    //       : values?.days_of_month,
    // };

    try {
      const res = await patchData({
        endpoint: "Project/editProject",
        token: cookies.e_t,
        data: {
          ...values,
          working_hours: values.working_hours
            ? Number(values.working_hours)
            : null,
          description: values.description,
          business_segment_id: values.business_segment,
          manager_id: null,
        },
        params: { project_id: initialData?.id },
      });

      if (res.status) {
        toast.success("Project Updated Successfully", {
          style: confimationStyles,
          duration: 1000,
        });
        refetchData();
      }
      onClose();
    } catch (error) {
      toast.error("Error Updating Project", { style: errorStyles });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, { resetForm }) => {
        if (initialData) {
          handleEdit(values);
        } else {
          handleSubmit(values, { resetForm });
        }

        onFormDirty(false); // Reset form dirty state
      }}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({
        values,
        setFieldValue,
        touched,
        errors,
        setFieldTouched,
        handleBlur,
        dirty,
        validateForm,
      }) => (
        <>
          <FormObserver onFormDirty={onFormDirty} />
          <Form className="mt-3">
            {/* Name & Employee ID */}

            <div className="row gx-4">
              <InputLayout>
                <label className="required-field">Project Name</label>
                <Field name="name" />
                <Error error={errors.name} />
              </InputLayout>

              <InputLayout>
                <label className="required-field">Project Owner</label>
                <SelectDropdown
                  data={employeeList}
                  onChange={(option) => setFieldValue("owner_ids", option)}
                  selectedValue={values.owner_ids}
                  multiSelect={true}
                  onClear={() => setFieldValue("owner_ids", [])}
                  isError={touched.owner_ids && errors.owner_ids}
                />
                <Error error={errors.owner_ids} />
              </InputLayout>
            </div>

            <div className="row gx-4 mt-4">
              <InputLayout>
                <label className="required-field">Priority</label>
                <SelectDropdown
                  isClearable={true}
                  onClear={() => setFieldValue("priority", null)}
                  data={Priorities}
                  onChange={(option) => setFieldValue("priority", option)}
                  selectedValue={values.priority}
                />
                <Error error={errors.priority} />
              </InputLayout>

              {/* <InputLayout>
                <label className="required-field">Client Name</label>
                <ClientDropdown
                  clientOptions={client}
                  onClientListChange={setClient} 
                  values={values}
                  setFieldValue={setFieldValue}
                  isError={touched.client_id && errors.client_id}
                  refetchClientList={refetchClientList} 
                />
              </InputLayout> */}
              <InputLayout>
                <label className="required-field">Status</label>
                <SelectDropdown
                  isClearable={true}
                  onClear={() => setFieldValue("status", null)}
                  data={ProjectStatus}
                  onChange={(option) => setFieldValue("status", option)}
                  selectedValue={values.status}
                />
                <Error error={errors.status} />
              </InputLayout>
            </div>

            <div className="row gx-4 mt-4">
              <InputLayout>
                <label className="">Assigned to</label>
                <SelectDropdown
                  data={employeeList}
                  multiSelect={true}
                  onChange={(option) => setFieldValue("employee_id", option)}
                  selectedValue={values.employee_id}
                  onClear={() => setFieldValue("employee_id", [])}
                  sError={touched.employee_id && errors.employee_id}
                />
              </InputLayout>

              <InputLayout>
                <label className="">Assigned team</label>
                <TeamDropdown
                  allTeams={teamList}
                  teamOptions={teams}
                  onTeamListChange={setTeams}
                  values={values}
                  setFieldValue={setFieldValue}
                  isError={touched.team_assigned && errors.team_assigned}
                  refetchTeamList={refetchTeamList}
                  fieldName="team_assigned"
                />
              </InputLayout>
            </div>

            <div className="row gx-4 mt-4">
              <InputLayout>
                <label>Business Segment</label>
                <SelectDropdown
                  isClearable={true}
                  onClear={() => setFieldValue("business_segment", null)}
                  data={BusinessSegmentList}
                  onChange={(option) =>
                    setFieldValue("business_segment", option)
                  }
                  selectedValue={values.business_segment}
                />
                <Error error={errors.business_segment} />
              </InputLayout>

              {/* <InputLayout> */}
              {/* <label>Send Report To (Every Monday)</label> */}

              {/* Email Input Field */}
              {/* <div className="position-relative">
                  <Field
                    as="input"
                    id="email-input-field"
                    type="text"
                    className="form-control"
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      setEmailError("");
                    }}
                    onKeyDown={(e) =>
                      handleEmailKeyDown(
                        e,
                        values,
                        setFieldValue,
                        setFieldTouched
                      )
                    }
                    placeholder="Type email and press Enter"
                    style={{
                      fontSize: "14px",

                      fontWeight: "400",
                      color: "#212529",
                      lineHeight: "1.5",
                      border: "1px solid #ced4da",
                      borderRadius: "4px",
                      padding: "6px 12px",
                      backgroundColor: "#fff",
                      boxShadow: "none",
                      outline: "none",
                      transition: "border-color 0.2s ease-in-out",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#6C5DD3")}
                    onBlur={(e) => (e.target.style.borderColor = "#ced4da")}
                  />
                </div> */}

              {/* Inline validation error */}
              {/* {emailError && (
                  <div
                    className="text-danger"
                    style={{ fontSize: "12px", marginTop: "4px" }}
                  >
                    {emailError}
                  </div>
                )}
                <Error error={errors.alert_email} /> */}

              {/* Display entered emails below input */}
              {/* {values.alert_email.length > 0 && (
                  <div
                    className="form-control mt-2"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "6px",
                      alignItems: "center",
                      padding: "6px 10px",
                      minHeight: "42px",
                      // backgroundColor: "#fff",
                      border: "1px solid #ced4da",
                      // borderRadius: "4px",
                      boxShadow: "none",
                      fontSize: "14px",

                      color: "#212529",
                      lineHeight: "1.5",
                      overflowY: "auto",
                      maxHeight: "120px",
                    }}
                  >
                    {values.alert_email.map((email, index) => (
                      <span
                        key={index}
                        className="badge  text-dark border"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          fontWeight: "400",
                          padding: "6px 10px",
                          fontSize: "13px",

                          lineHeight: "1.4",
                          // borderRadius: "20px",
                        }}
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() =>
                            removeEmail(index, values, setFieldValue)
                          }
                          style={{
                            background: "none",
                            border: "none",
                            fontSize: "16px",
                            lineHeight: "1",
                            cursor: "pointer",
                            color: "#555",
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )} */}
              {/* </InputLayout> */}
            </div>
            <div className="mt-3">
              <InputLayout>
                <QuillEditor
                  label="Description"
                  value={values.description}
                  onChange={(value) => setFieldValue("description", value)}
                  onBlur={() => setFieldTouched("description", true)}
                  placeholderText="Enter project description..."
                  height="180px"
                  hp="70%"
                  className="quill-editor-custom"
                />
                <Error error={errors.description} />
              </InputLayout>
            </div>
            <div className="row gx-4 mt-4">
              <label className="header-title">Project Report Frequency</label>

              <div className="row gx-4 mt-2">
                <InputLayout>
                  <label className="">Report Frequency </label>
                  <SelectDropdown
                    data={frequencyOptions}
                    isClearable={true}
                    onChange={(option) => {
                      setFieldValue("frequency", option);

                      // Reset other dependent fields
                      setFieldValue("days_of_week", []);
                      setFieldValue("days_of_month", []);
                      setTimeout(() => {
                        setFieldTouched("frequency", true);
                        setFieldTouched("time_of_day", true);
                        setFieldTouched("trigger_start_date", true);
                        validateForm();
                      }, 20);
                    }}
                    selectedValue={values.frequency}
                    onClear={() => {
                      setFieldValue("frequency", null);
                      setFieldValue("days_of_week", []);
                      setFieldValue("days_of_month", []);
                      setFieldValue("time_of_day", null);
                      setFieldValue("trigger_end_date", null);
                      setFieldValue("trigger_start_date", null);
                    }}
                    isError={touched.frequency && errors.frequency}
                    errors={errors.frequency}
                    touched={touched.frequency}
                  />
                  <Error error={errors.frequency} />
                </InputLayout>

                {["weekly", "biweekly"].includes(values.frequency) && (
                  // <div className="row gx-4 mt-4">
                  <InputLayout>
                    <label className="required-field">
                      Select Days of Week
                    </label>
                    <SelectDropdown
                      data={daysOfWeekOptions}
                      multiSelect={true}
                      onChange={(option) => {
                        setFieldValue("days_of_week", option);
                        // Force validation
                        setTimeout(() => {
                          setFieldTouched("days_of_week", true);
                          validateForm();
                        }, 20);
                      }}
                      selectedValue={values.days_of_week}
                      onClear={() => setFieldValue("days_of_week", [])}
                      isError={touched.days_of_week && errors.days_of_week}
                    />

                    <Error error={errors.days_of_week} />
                  </InputLayout>
                  // </div>
                )}
                {["monthly", "fortnightly"].includes(values.frequency) && (
                  // <div className="row gx-4 mt-4">
                  <InputLayout>
                    <label className="required-field">
                      Select Date of Month
                    </label>
                    <SelectDropdown
                      data={
                        values.frequency === "fortnightly"
                          ? daysOfMonthOptions
                          : daysOfMonthOptions
                      }
                      multiSelect={true}
                      onChange={(option) => {
                        setFieldValue("days_of_month", option);
                        // Force validation
                        setTimeout(() => {
                          setFieldTouched("days_of_month", true);
                          validateForm();
                        }, 20);
                      }}
                      selectedValue={values.days_of_month}
                      onClear={() => setFieldValue("days_of_month", [])}
                      isError={touched.days_of_month && errors.days_of_month}
                    />
                    <Error error={errors.days_of_month} />
                  </InputLayout>
                  // </div>
                )}
              </div>
              {frequencyOptions
                .map((o) => o.value)
                .includes(values.frequency) && (
                <div className="row gx-4 mt-3">
                  <InputLayout>
                    <label htmlFor="" className="required-field">
                      Trigger Start Date
                    </label>
                    <DateSelector
                      placeholder=""
                      selectedDate={values?.trigger_start_date}
                      onChange={(date) => {
                        const istDate = new Date(date);
                        const utcDate = new Date(
                          istDate.getTime() -
                            istDate.getTimezoneOffset() * 60000
                        );
                        setFieldValue(
                          "trigger_start_date",
                          utcDate.toISOString()
                        );
                        setTimeout(() => {
                          setFieldTouched("trigger_start_date", true);
                          validateForm();
                        }, 20);
                      }}
                      handleBlur={(e) => {
                        setFieldTouched("trigger_start_date", true);
                        handleBlur(e);
                      }}
                      disablePast={isAdmin ? false : true}
                      disabled={initialData?.is_recurring}
                      isError={
                        touched.trigger_start_date && errors.trigger_start_date
                      }
                    />
                    <Error error={errors.trigger_start_date} />
                  </InputLayout>
                  <InputLayout>
                    <label htmlFor="">Trigger End Date</label>
                    <DateSelector
                      placeholder=""
                      selectedDate={values?.trigger_end_date}
                      onChange={(date) => {
                        const istDate = new Date(date);
                        const utcDate = new Date(
                          istDate.getTime() -
                            istDate.getTimezoneOffset() * 60000
                        );
                        setFieldValue(
                          "trigger_end_date",
                          utcDate.toISOString()
                        );
                        setTimeout(() => {
                          setFieldTouched("trigger_end_date", true);
                          validateForm();
                        }, 20);
                      }}
                      handleBlur={(e) => {
                        setFieldTouched("trigger_end_date", true);
                        handleBlur(e);
                      }}
                      disablePast={isAdmin ? false : true}
                      isError={
                        touched.trigger_end_date && errors.trigger_end_date
                      }
                    />
                    <Error error={errors.trigger_end_date} />
                  </InputLayout>
                </div>
              )}
              {frequencyOptions
                .map((o) => o.value)
                .includes(values.frequency) && (
                <InputLayout>
                  <div
                    className="d-flex flex-column mt-3"
                    style={{ width: "45%" }}
                  >
                    <label className="required-field mb-1 me-3">
                      Time of day
                    </label>
                    <div className="flex-grow-1">
                      <MuiTimePicker
                        onChange={(minutes) => {
                          setFieldValue(
                            "time_of_day",
                            convertMinutesToHHMMSS(minutes)
                          );
                          setTimeout(() => {
                            setFieldTouched("time_of_day", true);
                            validateForm();
                          }, 20);
                        }}
                        handleBlur={(e) => {
                          setFieldTouched("time_of_day", true);
                          handleBlur(e);
                        }}
                        selectedTime={values?.time_of_day}
                        isError={touched.time_of_day && errors.time_of_day}
                      />
                      <Error error={errors.time_of_day} />
                    </div>
                  </div>
                </InputLayout>
              )}
            </div>

            {/* <div className="row gx-4 mt-3">
              <InputLayout>
                <label className="required-field">Status</label>
                <SelectDropdown
                  isClearable={true}
                  onClear={() => setFieldValue("status", null)}
                  data={ProjectStatus}
                  onChange={(option) => setFieldValue("status", option)}
                  selectedValue={values.status}
                />
                <Error error={errors.status} />
              </InputLayout>
              <InputLayout></InputLayout>
            </div> */}

            {/* <div className=" mt-3">
              <InputLayout>
                <label className="">Description</label>
                <Field name="description" as="textarea" rows={2} />
                <Error error={errors.description} />
              </InputLayout>
             </div> */}

            <div className="drawer-footer mt-4">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !dirty}
              >
                {initialData ? "Update" : "Save"}
              </button>
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
};

const FormObserver = ({ onFormDirty }) => {
  const { dirty } = useFormikContext();
  useEffect(() => onFormDirty(dirty), [dirty, onFormDirty]);
  return null;
};

export default ProjectsForm;
